import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api, formatEUR } from "@/lib/api";

const STEPS = ["Service", "Property", "Schedule", "Contact", "Review"];

const TIME_SLOTS = ["08:00", "09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00", "17:00"];

export default function Booking() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [step, setStep] = useState(0);
    const [services, setServices] = useState([]);
    const [extrasCatalog, setExtrasCatalog] = useState([]);
    const [frequencies, setFrequencies] = useState([]);
    const [quote, setQuote] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [refStatus, setRefStatus] = useState(null); // { valid, name } | null

    const [form, setForm] = useState({
        service: searchParams.get("service") || "home",
        bedrooms: Number(searchParams.get("bedrooms") || 2),
        bathrooms: Number(searchParams.get("bathrooms") || 1),
        hours: Number(searchParams.get("hours") || 2),
        frequency: searchParams.get("frequency") || "one_off",
        extras: (searchParams.get("extras") || "").split(",").filter(Boolean),
        date: "",
        time_slot: "",
        full_name: "",
        email: "",
        phone: "",
        address: "",
        city: "Dublin",
        eircode: "",
        notes: "",
        referral_code: (searchParams.get("ref") || "").toUpperCase(),
        voucher_code: (searchParams.get("voucher") || "").toUpperCase(),
    });

    useEffect(() => {
        api.get("/services").then((res) => {
            setServices(res.data.services);
            setExtrasCatalog(res.data.extras);
            setFrequencies(res.data.frequencies);
        });
    }, []);

    useEffect(() => {
        if (!services.length) return;
        api.post("/quote", {
            service: form.service,
            bedrooms: form.bedrooms,
            bathrooms: form.bathrooms,
            frequency: form.frequency,
            extras: form.extras,
            hours: form.hours,
            referral_code: form.referral_code,
            voucher_code: form.voucher_code,
            email: form.email,
        }).then((res) => {
            setQuote(res.data);
            if (form.referral_code) {
                setRefStatus(res.data.referral_code_valid
                    ? { valid: true, name: res.data.referrer_first_name }
                    : { valid: false });
            } else {
                setRefStatus(null);
            }
        });
    }, [form.service, form.bedrooms, form.bathrooms, form.frequency, form.extras, form.hours, form.referral_code, form.voucher_code, form.email, services.length]);

    const update = (patch) => setForm((f) => ({ ...f, ...patch }));
    const toggleExtra = (k) => update({ extras: form.extras.includes(k) ? form.extras.filter((x) => x !== k) : [...form.extras, k] });

    const today = useMemo(() => new Date().toISOString().split("T")[0], []);

    const canNext = () => {
        if (step === 0) return !!form.service;
        if (step === 1) return form.bedrooms >= 0 && form.bathrooms >= 0;
        if (step === 2) return form.date && form.time_slot;
        if (step === 3) return form.full_name && /\S+@\S+\.\S+/.test(form.email) && form.phone && form.address && form.city;
        return true;
    };

    const handleNext = () => {
        if (!canNext()) {
            toast.error("Please complete the highlighted fields.");
            return;
        }
        setStep((s) => Math.min(STEPS.length - 1, s + 1));
    };
    const handleBack = () => setStep((s) => Math.max(0, s - 1));

    const submit = async () => {
        setSubmitting(true);
        try {
            const res = await api.post("/bookings", form);
            toast.success("Booking confirmed! Check your email.");
            navigate(`/booking/success/${res.data.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to submit booking");
        } finally {
            setSubmitting(false);
        }
    };

    const currentService = services.find((s) => s.key === form.service);

    return (
        <div data-testid="booking-page" className="min-h-screen bg-cream">
            <Navbar variant="solid" />

            <section className="pt-32 pb-20 px-6 md:px-12">
                <div className="max-w-6xl mx-auto">
                    <div className="mb-12 text-center">
                        <span className="uppercase tracking-[0.3em] text-xs font-bold text-gold-dark">— Online Booking</span>
                        <h1 className="font-playfair text-4xl md:text-5xl lg:text-6xl text-navy mt-4 tracking-tight">
                            Book Your <span className="italic text-gold-dark">Signature</span> Clean
                        </h1>
                    </div>

                    {/* Stepper */}
                    <div className="flex items-center justify-center mb-12 flex-wrap gap-y-3">
                        {STEPS.map((label, i) => (
                            <div key={label} className="flex items-center">
                                <div
                                    className={`w-9 h-9 flex items-center justify-center border font-outfit text-sm transition-all ${
                                        i === step
                                            ? "bg-navy text-gold border-gold"
                                            : i < step
                                            ? "bg-gold text-navy border-gold"
                                            : "bg-transparent text-muted-foreground border-cream-border"
                                    }`}
                                    data-testid={`stepper-${i}`}
                                >
                                    {i < step ? <CheckCircle2 className="w-4 h-4" /> : i + 1}
                                </div>
                                <span className={`ml-2 mr-4 font-outfit text-xs uppercase tracking-wider hidden sm:inline ${i === step ? "text-navy" : "text-muted-foreground"}`}>
                                    {label}
                                </span>
                                {i < STEPS.length - 1 && <div className={`w-6 sm:w-10 h-px ${i < step ? "bg-gold" : "bg-cream-border"} mr-4`} />}
                            </div>
                        ))}
                    </div>

                    <div className="grid lg:grid-cols-3 gap-10">
                        {/* Step Content */}
                        <div className="lg:col-span-2 bg-white border border-cream-border p-8 md:p-10">
                            {step === 0 && (
                                <div data-testid="step-service">
                                    <h2 className="font-playfair text-2xl text-navy mb-2">Choose your service</h2>
                                    <p className="font-outfit text-sm text-muted-foreground mb-8">Select the type of clean you need.</p>
                                    <div className="grid sm:grid-cols-2 gap-4">
                                        {services.map((s) => (
                                            <button
                                                key={s.key}
                                                data-testid={`step-service-${s.key}`}
                                                onClick={() => update({ service: s.key })}
                                                className={`text-left p-5 border transition-all ${
                                                    form.service === s.key ? "border-gold bg-gold/5" : "border-cream-border hover:border-gold/60"
                                                }`}
                                            >
                                                <div className="font-playfair text-lg text-navy">{s.name}</div>
                                                <div className="text-xs text-muted-foreground mt-1 font-outfit">{s.description}</div>
                                                <div className="text-sm text-gold-dark mt-3 font-outfit font-semibold">{s.fixed_label}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {step === 1 && (
                                <div data-testid="step-property">
                                    <h2 className="font-playfair text-2xl text-navy mb-2">Tell us about your property</h2>
                                    <p className="font-outfit text-sm text-muted-foreground mb-8">Adjust the size and frequency to refine your quote.</p>

                                    <div className="space-y-8">
                                        {form.service === "hourly" ? (
                                            <div>
                                                <label className="uppercase tracking-[0.2em] text-xs font-bold text-navy mb-3 block">Hours needed <span className="text-muted-foreground normal-case tracking-normal text-[10px]">(€30 per hour)</span></label>
                                                <div className="flex flex-wrap gap-2">
                                                    {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
                                                        <button
                                                            key={n}
                                                            data-testid={`prop-hours-${n}`}
                                                            onClick={() => update({ hours: n })}
                                                            className={`w-11 h-11 border font-outfit text-sm ${form.hours === n ? "border-gold bg-gold text-navy" : "border-cream-border text-navy hover:border-gold"}`}
                                                        >
                                                            {n}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        ) : (
                                            <>
                                        <div>
                                            <label className="uppercase tracking-[0.2em] text-xs font-bold text-navy mb-3 block">Bedrooms</label>
                                            <div className="flex flex-wrap gap-2">
                                                {[0, 1, 2, 3, 4, 5, 6].map((n) => (
                                                    <button
                                                        key={n}
                                                        data-testid={`prop-bedroom-${n}`}
                                                        onClick={() => update({ bedrooms: n })}
                                                        className={`w-11 h-11 border font-outfit text-sm ${form.bedrooms === n ? "border-gold bg-gold text-navy" : "border-cream-border text-navy hover:border-gold"}`}
                                                    >
                                                        {n}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="uppercase tracking-[0.2em] text-xs font-bold text-navy mb-3 block">Bathrooms</label>
                                            <div className="flex flex-wrap gap-2">
                                                {[0, 1, 2, 3, 4, 5].map((n) => (
                                                    <button
                                                        key={n}
                                                        data-testid={`prop-bathroom-${n}`}
                                                        onClick={() => update({ bathrooms: n })}
                                                        className={`w-11 h-11 border font-outfit text-sm ${form.bathrooms === n ? "border-gold bg-gold text-navy" : "border-cream-border text-navy hover:border-gold"}`}
                                                    >
                                                        {n}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                            </>
                                        )}
                                        <div>
                                            <label className="uppercase tracking-[0.2em] text-xs font-bold text-navy mb-3 block">Frequency</label>
                                            <div className="grid sm:grid-cols-2 gap-2">
                                                {frequencies.map((f) => (
                                                    <button
                                                        key={f.key}
                                                        data-testid={`prop-freq-${f.key}`}
                                                        onClick={() => update({ frequency: f.key })}
                                                        className={`px-4 py-3 border font-outfit text-sm ${form.frequency === f.key ? "border-gold bg-gold text-navy" : "border-cream-border text-navy hover:border-gold"}`}
                                                    >
                                                        {f.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                        <div>
                                            <label className="uppercase tracking-[0.2em] text-xs font-bold text-navy mb-3 block">Extras (optional)</label>
                                            <div className="flex flex-wrap gap-2">
                                                {extrasCatalog.map((e) => (
                                                    <button
                                                        key={e.key}
                                                        data-testid={`prop-extra-${e.key}`}
                                                        onClick={() => toggleExtra(e.key)}
                                                        className={`px-4 py-2 border font-outfit text-sm ${form.extras.includes(e.key) ? "border-gold bg-gold/10 text-gold-dark" : "border-cream-border text-navy hover:border-gold"}`}
                                                    >
                                                        {e.label} <span className="opacity-70 ml-1">+€{e.price}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {step === 2 && (
                                <div data-testid="step-schedule">
                                    <h2 className="font-playfair text-2xl text-navy mb-2">Schedule your clean</h2>
                                    <p className="font-outfit text-sm text-muted-foreground mb-8">Pick the date and time that work best for you.</p>

                                    <div className="space-y-8">
                                        <div>
                                            <label className="uppercase tracking-[0.2em] text-xs font-bold text-navy mb-3 block">Preferred Date</label>
                                            <input
                                                data-testid="schedule-date"
                                                type="date"
                                                min={today}
                                                value={form.date}
                                                onChange={(e) => update({ date: e.target.value })}
                                                className="w-full max-w-sm border border-cream-border bg-white px-4 py-3 font-outfit text-navy focus:outline-none focus:border-gold"
                                            />
                                        </div>
                                        <div>
                                            <label className="uppercase tracking-[0.2em] text-xs font-bold text-navy mb-3 block">Time Slot</label>
                                            <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                                                {TIME_SLOTS.map((t) => (
                                                    <button
                                                        key={t}
                                                        data-testid={`schedule-time-${t}`}
                                                        onClick={() => update({ time_slot: t })}
                                                        className={`px-2 py-3 border font-outfit text-sm ${form.time_slot === t ? "border-gold bg-gold text-navy" : "border-cream-border text-navy hover:border-gold"}`}
                                                    >
                                                        {t}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {step === 3 && (
                                <div data-testid="step-contact">
                                    <h2 className="font-playfair text-2xl text-navy mb-2">Your contact details</h2>
                                    <p className="font-outfit text-sm text-muted-foreground mb-8">We'll send your confirmation here.</p>

                                    <div className="grid sm:grid-cols-2 gap-5">
                                        <Field label="Full Name" testid="contact-name" value={form.full_name} onChange={(v) => update({ full_name: v })} />
                                        <Field label="Email" testid="contact-email" type="email" value={form.email} onChange={(v) => update({ email: v })} />
                                        <Field label="Phone" testid="contact-phone" value={form.phone} onChange={(v) => update({ phone: v })} />
                                        <Field label="City" testid="contact-city" value={form.city} onChange={(v) => update({ city: v })} />
                                        <div className="sm:col-span-2">
                                            <Field label="Address" testid="contact-address" value={form.address} onChange={(v) => update({ address: v })} />
                                        </div>
                                        <Field label="Eircode (optional)" testid="contact-eircode" value={form.eircode} onChange={(v) => update({ eircode: v })} />
                                        <div className="sm:col-span-2">
                                            <label className="uppercase tracking-[0.2em] text-xs font-bold text-navy mb-2 block">Notes (optional)</label>
                                            <textarea
                                                data-testid="contact-notes"
                                                value={form.notes}
                                                onChange={(e) => update({ notes: e.target.value })}
                                                rows={3}
                                                className="w-full border border-cream-border bg-white px-4 py-3 font-outfit text-navy focus:outline-none focus:border-gold"
                                                placeholder="Anything we should know? Pets, access, special requests…"
                                            />
                                        </div>
                                        <div className="sm:col-span-2">
                                            <label className="uppercase tracking-[0.2em] text-xs font-bold text-navy mb-2 block">Referral code (optional)</label>
                                            <div className="flex gap-2">
                                                <input
                                                    data-testid="contact-referral"
                                                    type="text"
                                                    value={form.referral_code}
                                                    onChange={(e) => update({ referral_code: e.target.value.toUpperCase() })}
                                                    placeholder="SHINE-XXXXXX"
                                                    className="w-full border border-cream-border bg-white px-4 py-3 font-outfit text-navy focus:outline-none focus:border-gold uppercase tracking-wider"
                                                />
                                            </div>
                                            {form.referral_code && refStatus && (
                                                <p
                                                    data-testid="referral-status"
                                                    className={`mt-2 text-xs font-outfit ${refStatus.valid ? "text-gold-dark" : "text-red-600"}`}
                                                >
                                                    {refStatus.valid
                                                        ? `Nice — €10 off, courtesy of ${refStatus.name || "a friend"}.`
                                                        : "That code isn&apos;t recognised. Double-check and try again."}
                                                </p>
                                            )}
                                            {quote?.credit_available && (
                                                <p data-testid="credit-notice" className="mt-2 text-xs font-outfit text-gold-dark">
                                                    You have a free-room credit — €{quote.credit_applied.toFixed(2)} auto-applied at checkout.
                                                </p>
                                            )}
                                        </div>
                                        <div className="sm:col-span-2">
                                            <label className="uppercase tracking-[0.2em] text-xs font-bold text-navy mb-2 block">Reward voucher (optional)</label>
                                            <input
                                                data-testid="contact-voucher"
                                                type="text"
                                                value={form.voucher_code}
                                                onChange={(e) => update({ voucher_code: e.target.value.toUpperCase() })}
                                                placeholder="REWARD-XXXXXX"
                                                className="w-full border border-cream-border bg-white px-4 py-3 font-outfit text-navy focus:outline-none focus:border-gold uppercase tracking-wider"
                                            />
                                            {form.voucher_code && quote && (
                                                <p
                                                    data-testid="voucher-status"
                                                    className={`mt-2 text-xs font-outfit ${quote.voucher_code_valid ? "text-gold-dark" : "text-red-600"}`}
                                                >
                                                    {quote.voucher_code_valid
                                                        ? `${quote.voucher_label} unlocked — €${quote.voucher_discount.toFixed(2)} off.`
                                                        : "Voucher not valid for this email, already used, or unknown."}
                                                </p>
                                            )}
                                            <p className="mt-2 text-[11px] font-outfit text-muted-foreground">
                                                Don't have one? <Link to={`/rewards?email=${encodeURIComponent(form.email)}`} className="underline underline-offset-2 hover:text-gold-dark" data-testid="rewards-link">View your loyalty rewards</Link>.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {step === 4 && (
                                <div data-testid="step-review">
                                    <h2 className="font-playfair text-2xl text-navy mb-6">Review & confirm</h2>
                                    <dl className="divide-y divide-cream-border font-outfit text-sm">
                                        <Row label="Service" value={currentService?.name || form.service} />
                                        {form.service === "hourly" ? (
                                            <Row label="Duration" value={`${form.hours} hour${form.hours > 1 ? "s" : ""} @ €30/hr`} />
                                        ) : (
                                            <Row label="Bedrooms / Bathrooms" value={`${form.bedrooms} / ${form.bathrooms}`} />
                                        )}
                                        <Row label="Frequency" value={frequencies.find((f) => f.key === form.frequency)?.label || form.frequency} />
                                        <Row label="Extras" value={form.extras.length ? form.extras.map((k) => extrasCatalog.find((e) => e.key === k)?.label).join(", ") : "None"} />
                                        <Row label="Date / Time" value={`${form.date} @ ${form.time_slot}`} />
                                        <Row label="Name" value={form.full_name} />
                                        <Row label="Email" value={form.email} />
                                        <Row label="Phone" value={form.phone} />
                                        <Row label="Address" value={`${form.address}, ${form.city} ${form.eircode}`} />
                                        {form.notes && <Row label="Notes" value={form.notes} />}
                                        {quote?.referral_code_valid && (
                                            <Row label="Referral code" value={`${form.referral_code} (−${formatEUR(quote.referral_discount)})`} />
                                        )}
                                        {quote?.credit_applied > 0 && (
                                            <Row label="Free-room credit" value={`−${formatEUR(quote.credit_applied)}`} />
                                        )}
                                        {quote?.voucher_code_valid && (
                                            <Row label="Reward voucher" value={`${form.voucher_code} (−${formatEUR(quote.voucher_discount)})`} />
                                        )}
                                    </dl>
                                </div>
                            )}

                            {/* Nav buttons */}
                            <div className="mt-10 flex items-center justify-between">
                                <button
                                    data-testid="step-back-btn"
                                    onClick={handleBack}
                                    disabled={step === 0}
                                    className="inline-flex items-center gap-2 px-5 py-3 border border-cream-border text-navy font-outfit text-sm uppercase tracking-wider hover:border-gold disabled:opacity-30 disabled:cursor-not-allowed"
                                >
                                    <ArrowLeft className="w-4 h-4" /> Back
                                </button>
                                {step < STEPS.length - 1 ? (
                                    <button
                                        data-testid="step-next-btn"
                                        onClick={handleNext}
                                        className="inline-flex items-center gap-2 bg-navy text-gold border border-gold px-6 py-3 font-outfit text-sm uppercase tracking-wider hover:bg-gold hover:text-navy transition-colors"
                                    >
                                        Next <ArrowRight className="w-4 h-4" />
                                    </button>
                                ) : (
                                    <button
                                        data-testid="step-submit-btn"
                                        disabled={submitting}
                                        onClick={submit}
                                        className="inline-flex items-center gap-2 bg-gold text-navy px-8 py-3 font-outfit font-semibold text-sm uppercase tracking-wider hover:bg-gold-light disabled:opacity-50"
                                    >
                                        {submitting ? "Submitting…" : "Confirm Booking"} <Sparkles className="w-4 h-4" />
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Quote Sidebar */}
                        <div>
                            <div className="border border-navy/20 bg-navy text-cream p-8 sticky top-28">
                                <div className="flex items-center gap-2 mb-2">
                                    <Sparkles className="w-4 h-4 text-gold" />
                                    <span className="uppercase tracking-[0.2em] text-xs font-bold text-gold">Estimate</span>
                                </div>
                                <div className="font-playfair text-4xl md:text-5xl my-3" data-testid="booking-quote-total">
                                    {quote ? formatEUR(quote.total) : "—"}
                                </div>
                                <div className="gold-divider my-4" />
                                {quote && (
                                    <ul className="space-y-2 text-sm font-outfit text-cream/80">
                                        <li className="flex justify-between"><span>Service</span><span>{quote.service_name}</span></li>
                                        {quote.service === "hourly" && <li className="flex justify-between"><span>Rate</span><span>€{quote.hourly_rate}/hr × {quote.hours}h</span></li>}
                                        {quote.service !== "hourly" && <li className="flex justify-between"><span>Base</span><span>{formatEUR(quote.base_price)}</span></li>}
                                        {quote.bedrooms_cost > 0 && <li className="flex justify-between"><span>Bedrooms</span><span>{formatEUR(quote.bedrooms_cost)}</span></li>}
                                        {quote.bathrooms_cost > 0 && <li className="flex justify-between"><span>Bathrooms</span><span>{formatEUR(quote.bathrooms_cost)}</span></li>}
                                        {quote.extras_total > 0 && <li className="flex justify-between"><span>Extras</span><span>{formatEUR(quote.extras_total)}</span></li>}
                                        {quote.frequency_discount_multiplier < 1 && (
                                            <li className="flex justify-between text-gold">
                                                <span>Recurring discount</span>
                                                <span>−{Math.round((1 - quote.frequency_discount_multiplier) * 100)}%</span>
                                            </li>
                                        )}
                                        {quote.referral_discount > 0 && (
                                            <li className="flex justify-between text-gold" data-testid="quote-referral-line">
                                                <span>Referral bonus</span>
                                                <span>−{formatEUR(quote.referral_discount)}</span>
                                            </li>
                                        )}
                                        {quote.credit_applied > 0 && (
                                            <li className="flex justify-between text-gold" data-testid="quote-credit-line">
                                                <span>Free-room credit</span>
                                                <span>−{formatEUR(quote.credit_applied)}</span>
                                            </li>
                                        )}
                                        {quote.voucher_discount > 0 && (
                                            <li className="flex justify-between text-gold" data-testid="quote-voucher-line">
                                                <span>Loyalty voucher</span>
                                                <span>−{formatEUR(quote.voucher_discount)}</span>
                                            </li>
                                        )}
                                        {quote.points_will_earn > 0 && (
                                            <li className="flex justify-between text-cream/70 pt-2 border-t border-cream/10" data-testid="quote-points-line">
                                                <span>You&apos;ll earn</span>
                                                <span>{quote.points_will_earn} pts</span>
                                            </li>
                                        )}
                                    </ul>
                                )}
                                <p className="text-xs text-cream/50 mt-6 italic">Final price confirmed after walkthrough.</p>
                            </div>

                            <Link to="/" data-testid="back-home-link" className="block text-center mt-6 text-sm text-navy/70 hover:text-gold-dark font-outfit underline underline-offset-4">
                                Cancel and return home
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
}

function Field({ label, value, onChange, type = "text", testid }) {
    return (
        <div>
            <label className="uppercase tracking-[0.2em] text-xs font-bold text-navy mb-2 block">{label}</label>
            <input
                data-testid={testid}
                type={type}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="w-full border border-cream-border bg-white px-4 py-3 font-outfit text-navy focus:outline-none focus:border-gold"
            />
        </div>
    );
}

function Row({ label, value }) {
    return (
        <div className="flex justify-between py-3 gap-6">
            <dt className="uppercase tracking-wider text-xs text-muted-foreground">{label}</dt>
            <dd className="text-navy text-right">{value}</dd>
        </div>
    );
}
